import { Dayjs } from 'dayjs'
import { findRecordCoveringDate } from './timeline'
import {
  PayClassificationVariation,
  transformVariationOnGet,
} from './variation'
import { PayClassificationAward, PayClassificationType } from '$constants'
import {
  SelectOption,
  IPayClassificationResponse,
  IPayClassificationTimelineRecordResponse,
} from '$models'

export type {
  IPayClassificationResponse,
  IPutPayClassificationRequest,
  ICreatePayClassificationRequest,
  IPayClassificationTimelineRecordResponse,
  IPayClassificationTimelineRecordRequest,
} from '@loadedhub/core-api-sdk'
export {
  CreatePayClassificationRequest,
  PutPayClassificationRequest,
  PayClassificationTimelineRecordRequest,
} from '@loadedhub/core-api-sdk'

export interface PayClassificationTimelineRecord
  extends Omit<IPayClassificationTimelineRecordResponse, 'variations'> {
  variations: PayClassificationVariation[]
}

export interface PayClassification
  extends Omit<IPayClassificationResponse, 'timeline'> {
  timeline: PayClassificationTimelineRecord[]
}

export const PAY_CLASSIFICATION_TIMELINE_MIN_VALID_FROM = '1900-01-01'

const transformPayClassificationTimelineRecordOnGet = (
  record: IPayClassificationTimelineRecordResponse
): PayClassificationTimelineRecord => {
  return {
    ...record,
    variations: (record.variations ?? []).map(transformVariationOnGet),
  }
}

// Function to apply transformations to PayClassification object
export const transformPayClassificationOnGet = (
  payClassification: IPayClassificationResponse
): PayClassification => {
  return {
    ...payClassification,
    datestampDeleted: payClassification.datestampDeleted,
    timeline: (payClassification.timeline ?? []).map(
      transformPayClassificationTimelineRecordOnGet
    ),
  }
}

/**
 * Finds the timeline record holding a classification's rate and variations.
 * When a reference date is given, returns the record valid at that date,
 * otherwise falls back to the record active today.
 * @param timeline the classification's rate timeline
 * @param referenceDate optional date to resolve the record for
 * @returns the matching record, or undefined if none found
 */
export const getActivePayClassificationRecord = (
  timeline: PayClassificationTimelineRecord[] | undefined,
  referenceDate?: Dayjs
): PayClassificationTimelineRecord | undefined => {
  if (!timeline) return undefined

  // No reference date means "in effect now", which the server has already decided and flagged.
  if (!referenceDate) return timeline.find((record) => record.isActive)

  return findRecordCoveringDate(timeline, referenceDate)
}

export const newPayClassification: PayClassification = {
  name: '',
  type: PayClassificationType.Custom,
  award: PayClassificationAward.Custom,
  // A classification is created together with its first rate window, so the add form edits a
  // single-record timeline rather than a flat rate. The server anchors validFrom regardless.
  timeline: [
    {
      validFrom: PAY_CLASSIFICATION_TIMELINE_MIN_VALID_FROM,
      baseHourlyRate: 0,
      variations: [],
    },
  ],
}

export const getPayClassificationsAsOptions = (
  payClassifications: PayClassification[]
): SelectOption[] =>
  payClassifications.map((payClassification) => {
    return {
      label: payClassification.name,
      value: payClassification.id!,
    }
  })
